/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { isEmpty } from "lodash"
import { ROLE_OWNER, ROLE_STORE_MANAGER } from "troy-common/constants"
import { getUserRole } from "troy-common/utils"
import {
  Review,
  ReviewReplyReason,
  ReviewUnReportableReason,
} from "types/api/menuFeedback.type"
import { ActionType } from "types/models/feedback"

export const isRoleAllowedReply = () => {
  return [ROLE_OWNER, ROLE_STORE_MANAGER].includes(getUserRole())
}

export const canSubmitReply = (review: Review) => {
  return (
    review.unreplyableReason ===
    ReviewReplyReason.ReviewUnreplyableReasonReplyable
  )
}

export const canEditReply = (review: Review) => {
  let replyContent = null
  if (
    review.reviewReplies &&
    Array.isArray(review.reviewReplies) &&
    review.reviewReplies.length > 0
  ) {
    replyContent = review.reviewReplies[review.reviewReplies.length - 1]
  }

  if (!replyContent) return true // can still submit a new reply

  // use BE status to determine if edit is allowed
  return (
    replyContent.uneditableReason ===
    ReviewReplyReason.ReviewUnreplyableReasonReplyable
  )
}

export const canSubmitReport = (review: Review) => {
  // Return false immediately if description is missing or empty
  if (!review.description?.trim()) {
    return false
  }

  // Check unreportableReason if description is valid
  return (
    review.unreportableReason ===
    ReviewUnReportableReason.ReviewUnreportableReasonReportable
  )
}

export const determineFeedbackRowAction = (review: Review): ActionType => {
  // distinguish edit and reply drawer
  if (!isRoleAllowedReply()) return ActionType.SEE_DETAILS

  const hasReplied =
    review.reviewReplies &&
    Array.isArray(review.reviewReplies) &&
    review.reviewReplies.length > 0 &&
    review.reviewReplies.filter(reply => !isEmpty(reply)).length > 0

  if (!hasReplied && canSubmitReply(review)) return ActionType.ADD_REPLY

  if (hasReplied && canEditReply(review)) return ActionType.EDIT_REPLY

  return ActionType.SEE_DETAILS
}
